import { useT } from '../i18n';

export interface SendWindow {
  start: string;
  end: string;
}

/**
 * As faixas de horário em que as mensagens saem.
 *
 * O mesmo editor serve para a empresa (nas configurações) e para uma campanha específica, porque a
 * pergunta é a mesma — em que horas do dia isto pode sair — e a pessoa não deveria aprender duas telas.
 */
export function SendWindowsEditor({ windows, onChange, disabled, max = 6 }: {
  windows: SendWindow[];
  onChange: (windows: SendWindow[]) => void;
  disabled?: boolean;
  max?: number;
}) {
  const t = useT();

  const update = (index: number, patch: Partial<SendWindow>) =>
    onChange(windows.map((window, i) => (i === index ? { ...window, ...patch } : window)));

  return (
    <div className="space-y-3">
      {windows.map((window, index) => (
        <div key={index} className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-500 w-16">{t('win.period', { number: index + 1 })}</span>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">{t('win.from')}</label>
            <input
              type="time"
              value={window.start}
              disabled={disabled}
              onChange={event => update(index, { start: event.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">{t('win.to')}</label>
            <input
              type="time"
              value={window.end}
              disabled={disabled}
              onChange={event => update(index, { end: event.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {windows.length > 1 && !disabled && (
            <button
              type="button"
              onClick={() => onChange(windows.filter((_, i) => i !== index))}
              className="text-sm text-red-600 hover:text-red-800 ms-auto"
            >
              {t('win.remove')}
            </button>
          )}
        </div>
      ))}

      {windows.length < max && !disabled && (
        <button
          type="button"
          onClick={() => onChange([...windows, { start: '16:00', end: '21:00' }])}
          className="text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
        >
          + {t('win.add')}
        </button>
      )}
    </div>
  );
}

/** As duas validações que a API também faz, para avisar antes de enviar o formulário */
export function validateWindows(windows: SendWindow[]): 'EMPTY_TIME' | 'SAME_TIME' | null {
  for (const window of windows) {
    if (!window.start || !window.end) return 'EMPTY_TIME';
    if (window.start === window.end) return 'SAME_TIME';
  }
  return null;
}
